import { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Drag handle for the docked Buffy transcript (Sandy 2026-09-23): instead of
// hiding the conversation, the teacher drags its top edge to choose how much
// of the page it takes. One height for every teacher page, kept per browser.
// 2026-10-18 (Sandy): it drags all the way down to no chat at all (0). A drag
// that ends near the bottom snaps closed; the handle stays above the chat box
// to drag it back up, and a new message reopens it at its last open size.
// 2026-10-19 (audit W33/W34): the docked transcript takes at most 35% of the
// screen, unless the teacher drags it in this tab; a reply that arrives while
// it is smaller (or folded) grows it back to that size so the reply shows.
const KEY = 'tdv3.buffyChatHeight';
const OPEN_KEY = 'tdv3.buffyChatHeight.open';
const CHOSEN_KEY = 'tdv3.buffyChatHeight.chosen'; // sessionStorage: dragged in this tab
export const CHAT_MIN = 0;
export const CHAT_CAP = 0.35;
// Short screens (P8 follow-up, 2026-10-19): at 700px tall or less (a
// Chromebook is 650) the page dock goes compact and the WHOLE open dock keeps
// to half the screen. COMPACT_CHROME is what the compact dock spends outside
// the transcript (buffyChat.css: 12 top + 24 grip strip + 48 chat box + 8
// bottom); the transcript gets the rest. Same threshold as the CSS media query.
export const COMPACT_MAX_HEIGHT = 700;
export const COMPACT_DOCK_SHARE = 0.5;
export const COMPACT_CHROME = 92;
const SNAP = 56; // below this a drag closes the transcript instead of leaving a sliver
const STEP = 48;
const chatMax = () => Math.max(SNAP, window.innerHeight - 240);
const viewH = () => window.innerHeight || 800;
const isShort = () => viewH() <= COMPACT_MAX_HEIGHT;
let compactHost = false; // the page dock is mounted (the cap below depends on it)
const capPx = () => Math.round(compactHost && isShort() ? viewH() * COMPACT_DOCK_SHARE - COMPACT_CHROME : viewH() * CHAT_CAP);
const clamp = n => Math.round(Math.max(CHAT_MIN, Math.min(chatMax(), n)));
const snap = n => (n < SNAP ? 0 : n);

const readNumber = key => {
  try { const raw = localStorage.getItem(key); if (raw == null) return null; const n = Number(raw); return Number.isFinite(n) && n >= CHAT_MIN ? n : null; } catch { return null; }
};
const writeNumber = (key, n) => {
  try { if (n == null) localStorage.removeItem(key); else localStorage.setItem(key, String(n)); } catch { /* storage blocked: size lasts for this visit */ }
};
const chosenThisTab = () => { try { return sessionStorage.getItem(CHOSEN_KEY) === '1'; } catch { return false; } };
const markChosen = () => { try { sessionStorage.setItem(CHOSEN_KEY, '1'); } catch { /* storage blocked */ } };
// The size a reply needs: the default cap, or a taller size the teacher chose.
const roomy = n => (n > capPx() ? n : null);

// null = the default size (35% of the screen); 0 = closed. Closed is drawn by
// the stylesheet (logProps marks the transcript data-closed), not by inline
// padding: inline paddings outlived a reset. A size saved in an earlier visit
// stays under the 35% cap; a size dragged in this tab goes up to the screen
// less the composer. `folded`: start closed for this visit only (the teacher
// came from another page), without changing the saved size.
// logProps goes on the transcript itself: the attribute must be there on the
// first paint, and a ref set by a later sibling is not attached yet when the
// resizer's own effects run.
// compact: the host is the page dock (AIAssistant), which has the short-screen
// layout; other hosts keep the 35% transcript at every height.
const capCss = compact => (compact ? `calc(${COMPACT_DOCK_SHARE * 100}dvh - ${COMPACT_CHROME}px)` : `${CHAT_CAP * 100}dvh`);
export function useBuffyChatHeight({ folded = false, compactDock = false } = {}) {
  const [height, setHeight] = useState(() => (folded ? 0 : readNumber(KEY)));
  const [short, setShort] = useState(isShort);
  useEffect(() => {
    if (!compactDock) return undefined;
    compactHost = true;
    const onResize = () => setShort(isShort());
    window.addEventListener('resize', onResize);
    return () => { compactHost = false; window.removeEventListener('resize', onResize); };
  }, [compactDock]);
  const cap = capCss(compactDock && short);
  const style = height ? { maxHeight: chosenThisTab() ? `min(${height}px, calc(100dvh - 240px))` : `min(${height}px, ${cap})` } : { maxHeight: cap };
  const logProps = { style, 'data-closed': height === 0 ? '' : undefined };
  // A reply arrived: a transcript smaller than the cap (or closed) opens to it.
  const growForReply = () => setHeight(h => {
    if (h == null || h >= capPx()) return h;
    const next = h === 0 ? roomy(readNumber(OPEN_KEY)) : null;
    writeNumber(KEY, next);
    return next;
  });
  return { height, setHeight, style, logProps, growForReply };
}

export default function BuffyChatResizer({ logRef, height, setHeight }) {
  const drag = useRef(null);
  const [dragging, setDragging] = useState(false);
  const closed = height === 0;
  // The size on screen; 0 only when closed (an unmeasured log falls back to 96).
  const shown = () => logRef.current?.getBoundingClientRect().height || height || (closed ? 0 : 96);
  const atBottomNow = () => { const log = logRef.current; return !!log && log.scrollHeight - log.clientHeight - log.scrollTop < 4; };
  // Shrinking must not hide the newest reply: a transcript that was at its
  // bottom when the resize began stays there. Checked once per drag, and
  // applied after the new max-height reaches the DOM.
  const stick = useRef(false);
  // Closed: the stylesheet folds the transcript to nothing (buffyChat.css);
  // the host puts data-closed on it through useBuffyChatHeight's logProps.
  useLayoutEffect(() => {
    const log = logRef.current;
    if (stick.current && log) log.scrollTop = log.scrollHeight;
  }, [height, logRef]);
  const apply = (next, persist, atBottom = atBottomNow()) => {
    stick.current = atBottom;
    setHeight(next);
    if (persist) writeNumber(KEY, next);
  };
  // The size to come back to: the last open size the teacher chose when it is
  // above the cap, else the default (a sliver would show one line of a reply).
  const reopen = () => { stick.current = true; const open = roomy(readNumber(OPEN_KEY)); setHeight(open); writeNumber(KEY, open); };
  const rememberOpen = n => { if (n > 0) writeNumber(OPEN_KEY, Math.round(n)); };
  // Closed, a new message (the teacher's or Buffy's) brings the transcript back.
  useEffect(() => {
    const log = logRef.current;
    if (!closed || !log || typeof MutationObserver === 'undefined') return undefined;
    const watch = new MutationObserver(() => reopen());
    watch.observe(log, { childList: true });
    return () => watch.disconnect();
  }, [closed, logRef]); // eslint-disable-line react-hooks/exhaustive-deps
  const stop = () => {
    if (!drag.current) return;
    const { last, start } = drag.current;
    drag.current = null;
    setDragging(false);
    document.body.classList.remove('buffy-chat-resizing');
    // A click with no drag on the closed grip shows the conversation again.
    if (last == null) { if (closed) reopen(); return; }
    const final = snap(last);
    if (final !== last) apply(final, false, true);
    if (final === 0) rememberOpen(start);
    else rememberOpen(final);
    writeNumber(KEY, final);
  };
  const onPointerDown = e => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    markChosen();
    drag.current = { y: e.clientY, start: shown(), last: null, atBottom: closed || atBottomNow() };
    setDragging(true);
    document.body.classList.add('buffy-chat-resizing');
    // Belt and braces: a release that never reaches the grip (lost capture,
    // window switch) must still end the drag, or later mouse moves resize it.
    window.addEventListener('pointerup', stop, { once: true });
    window.addEventListener('blur', stop, { once: true });
  };
  const onPointerMove = e => {
    if (!drag.current) return;
    const next = clamp(drag.current.start + drag.current.y - e.clientY);
    drag.current.last = next;
    apply(next, false, drag.current.atBottom);
  };
  const onKeyDown = e => {
    const now = height ?? shown();
    const next = { ArrowUp: now + STEP, ArrowDown: now - STEP, Home: CHAT_MIN, End: chatMax() }[e.key];
    if (next == null) return;
    e.preventDefault();
    markChosen();
    // ArrowUp from closed reopens at the last open size (one step would snap shut again).
    if (e.key === 'ArrowUp' && now < SNAP) { reopen(); return; }
    const final = snap(clamp(next));
    if (final === 0) rememberOpen(now); else rememberOpen(final);
    apply(final, true, true);
  };
  const reset = () => { setHeight(null); writeNumber(KEY, null); };
  return <div className={`buffy-chat-resizer${closed ? ' is-closed' : ''}`} role="separator" aria-orientation="horizontal" tabIndex={0}
    aria-label={closed ? 'Conversation hidden. Drag up to show it' : 'Resize the conversation'} aria-valuemin={CHAT_MIN} aria-valuemax={chatMax()} aria-valuenow={Math.round(height ?? shown())}
    title={closed ? 'Drag up to show the conversation · double-click to reset' : 'Drag to resize, all the way down to hide · double-click to reset'} data-dragging={dragging}
    onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={stop} onPointerCancel={stop} onLostPointerCapture={stop}
    onKeyDown={onKeyDown} onDoubleClick={reset}><span aria-hidden="true" /></div>;
}
